import React, { useState } from 'react';
import { Box, Typography, TextField, Button, FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel, Paper } from '@mui/material';

interface JSONSchemaProperty {
  type: 'string' | 'number' | 'boolean' | 'array' | 'object';
  title?: string;
  description?: string;
  enum?: any[];
  default?: any;
}

interface JSONSchema {
  type: 'object';
  properties: Record<string, JSONSchemaProperty>;
  required?: string[];
  title?: string;
  description?: string;
}

interface UserInputRequest {
  schema: JSONSchema;
  data: Record<string, any>;
  description?: string;
  title?: string;
}

interface UserInputFormProps {
  inputRequest: UserInputRequest;
  onSubmit: (data: Record<string, any>) => void;
  loading?: boolean;
}

export default function UserInputForm({ inputRequest, onSubmit, loading = false }: UserInputFormProps) {
  const [formData, setFormData] = useState<Record<string, any>>(inputRequest.data);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    onSubmit(formData);
  };

  const renderField = (fieldName: string, property: JSONSchemaProperty) => {
    const value = formData[fieldName] || property.default || '';
    const isRequired = inputRequest.schema.required?.includes(fieldName);

    switch (property.type) {
      case 'string':
        if (property.enum) {
          return (
            <FormControl fullWidth key={fieldName} sx={{ mb: 2 }}>
              <InputLabel>{property.title || fieldName}</InputLabel>
              <Select
                value={value}
                onChange={(e) => handleChange(fieldName, e.target.value)}
                label={property.title || fieldName}
                required={isRequired}
              >
                {property.enum.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }
        return (
          <TextField
            key={fieldName}
            fullWidth
            label={property.title || fieldName}
            value={value}
            onChange={(e) => handleChange(fieldName, e.target.value)}
            helperText={property.description}
            required={isRequired}
            sx={{ mb: 2 }}
          />
        );

      case 'number':
        return (
          <TextField
            key={fieldName}
            fullWidth
            type="number"
            label={property.title || fieldName}
            value={value}
            onChange={(e) => handleChange(fieldName, Number(e.target.value))}
            helperText={property.description}
            required={isRequired}
            sx={{ mb: 2 }}
          />
        );

      case 'boolean':
        return (
          <FormControlLabel
            key={fieldName}
            control={
              <Checkbox
                checked={Boolean(value)}
                onChange={(e) => handleChange(fieldName, e.target.checked)}
              />
            }
            label={property.title || fieldName}
            sx={{ mb: 2, display: 'block' }}
          />
        );

      default:
        return (
          <TextField
            key={fieldName}
            fullWidth
            label={property.title || fieldName}
            value={JSON.stringify(value)}
            onChange={(e) => {
              try {
                handleChange(fieldName, JSON.parse(e.target.value));
              } catch {
                handleChange(fieldName, e.target.value);
              }
            }}
            helperText={property.description}
            required={isRequired}
            sx={{ mb: 2 }}
          />
        );
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        {inputRequest.title || inputRequest.schema.title || 'Additional Information Required'}
      </Typography>
      
      {(inputRequest.description || inputRequest.schema.description) && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {inputRequest.description || inputRequest.schema.description}
        </Typography>
      )}

      <Box component="form" onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
        {Object.entries(inputRequest.schema.properties).map(([fieldName, property]) =>
          renderField(fieldName, property)
        )}

        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={loading}
          sx={{ mt: 2 }}
        >
          {loading ? 'Submitting...' : 'Continue Workflow'}
        </Button>
      </Box>
    </Paper>
  );
}